import React, { useState } from 'react'
import { Badge, Form } from 'react-bootstrap'
import { Tag } from '../../../../types/models/tag'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'
import styled from 'styled-components'
import { useAppSelector } from '../../../../store/hooks'
import { useFetchProjectsQuery } from '../../../../services/projectsApi'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { Trash2, Bot } from 'lucide-react'
import ConfirmDialog from '../../../modals/ConfirmDialog'

interface TagsPanelProps {
  tags: Tag[]
  onRemoveAllTags: (tagId: string) => void
  filteredBoundingBoxes: AnnotationDataBoundingBox[]
  selectedBBoxClassIndex: number | undefined
  onTagSelect: (box: AnnotationDataBoundingBox) => void
  expandedTags: Set<string>
  onTagExpand: (tagId: string, event: React.MouseEvent) => void
  showBoxCount: boolean
  onDelete: (box: AnnotationDataBoundingBox) => void
  ai_labeled?: boolean
  // New: selection by tag support
  onToggleTagSelection?: (tagId: string, checked: boolean) => void
  selectedTagIds?: Set<string>
  selectionMode?: boolean
}

interface ColorRGB {
  r: number
  g: number
  b: number
}

const numberToHexColor = (colorNum: number): string => {
  return `#${colorNum.toString(16).padStart(6, '0').slice(0, 6)}`
}

const parseColor = (color: number | undefined): ColorRGB => {
  const defaultColor = '#666666'
  const hexColor = color ? numberToHexColor(color) : defaultColor
  const r = parseInt(hexColor.substring(1, 3), 16)
  const g = parseInt(hexColor.substring(3, 5), 16)
  const b = parseInt(hexColor.substring(5, 7), 16)
  return { r, g, b }
}

const TagGroup = styled.div<{ $isExpanded: boolean; color: number }>`
  background: rgba(26, 26, 26, 1);
  border: 2px solid
    ${(props) => {
      const { r, g, b } = parseColor(props.color)
      return `rgba(${r}, ${g}, ${b}, 0.25)`
    }};
  border-radius: 6px;
  margin-bottom: 0.5rem;
  overflow: hidden;
`

const TagHeader = styled.div<{ color: number }>`
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(26, 26, 26, 1);
  cursor: pointer;
  transition: background-color 0.2s;
  border-left: 3px solid ${(props) => numberToHexColor(props.color)};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const TagName = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
  display: inline-block;
  transform: ${(props) => (props.$isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`

const BoxCount = styled(Badge)`
  && {
    background: rgba(0, 0, 0, 0.3) !important;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.75rem;
    font-weight: 500;
    border: none;
  }
`

const TagContent = styled.div<{ $isExpanded: boolean }>`
  padding: ${(props) => (props.$isExpanded ? '0.5rem' : '0')};
  max-height: ${(props) => (props.$isExpanded ? '500px' : '0')};
  transition: all 0.2s ease-in-out;
  overflow: hidden;
`

const TagItem = styled.div<{ $isSelected: boolean; color: number }>`
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.25rem;
  border-radius: 4px;
  border: 1px solid ${(props) => numberToHexColor(props.color)};
  background: ${(props): string =>
    props.$isSelected
      ? `rgba(${parseColor(props.color).r}, ${parseColor(props.color).g}, ${
          parseColor(props.color).b
        }, 0.2)`
      : 'rgba(0, 0, 0, 0.2)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background: ${(props): string =>
      props.$isSelected
        ? `rgba(${parseColor(props.color).r}, ${parseColor(props.color).g}, ${
            parseColor(props.color).b
          }, 0.25)`
        : 'rgba(255, 255, 255, 0.1)'};
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const TagItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SmallAiIndicator = styled(Bot)`
  color: rgba(64, 156, 255, 1);
  opacity: 0.8;
`

const TagItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DeleteButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: rgba(255, 69, 58, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 69, 58, 0.2);
    color: rgba(255, 69, 58, 1);
  }
`

const ConfidenceBadge = styled(Badge)<{ color: number }>`
  && {
    background: rgba(0, 0, 0, 0.3) !important;
    color: ${(props): string => numberToHexColor(props.color)};
    font-size: 0.75rem;
    font-weight: 500;
    border: none;
  }
`

const AiBadge = styled(Badge)`
  && {
    background: rgba(64, 156, 255, 0.2) !important;
    color: rgba(64, 156, 255, 1);
    font-size: 0.75rem;
    font-weight: 500;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.35rem 0.5rem;
  }
`

export const TagsPanel: React.FC<TagsPanelProps> = ({
  tags,
  onRemoveAllTags,
  filteredBoundingBoxes,
  selectedBBoxClassIndex,
  onTagSelect,
  expandedTags,
  onTagExpand,
  showBoxCount,
  onDelete,
  ai_labeled,
  onToggleTagSelection,
  selectedTagIds,
  selectionMode,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: projectsData } = useFetchProjectsQuery({}, { skip: !selectedProjectId })
  const project = projectsData?.projects?.find((p) => p.id === selectedProjectId)
  const isClassificationProject = project?.annotation_type === 'classification'
  const [showConfirmDeleteAllTags, setShowConfirmDeleteAllTags] = useState(false)

  return (
    <>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>Annotations</h6>
          <div className="d-flex align-items-center gap-2">
            {ai_labeled && (
              <AiBadge>
                <Bot size={14} />
                AI
              </AiBadge>
            )}
            <Badge bg="secondary" pill>
              {filteredBoundingBoxes.length}
            </Badge>
          </div>
        </div>

        {/* Bounding Box Tags */}
        {filteredBoundingBoxes.length > 0 && (
          <div>
            {tags.map((tag) => {
              const tagBoxes = filteredBoundingBoxes.filter((box) => box.name === tag.id)
              if (tagBoxes.length === 0) return null

              const isExpanded = expandedTags.has(tag.id)
              const tagColor = tag.color || 0x666666

              if (isClassificationProject) {
                // For classification projects, show a simplified view
                return tagBoxes.map((box, index) => (
                  <TagItem
                    key={`${box.class_index}-${index}`}
                    $isSelected={selectedBBoxClassIndex === box.class_index}
                    color={tagColor}
                    onClick={() => onTagSelect(box)}
                  >
                    <span>{tag.name}</span>
                    {selectedBBoxClassIndex === box.class_index && (
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(box)
                        }}
                      >
                        <Trash2 size={16} />
                      </DeleteButton>
                    )}
                  </TagItem>
                ))
              }

              return (
                <TagGroup key={tag.id} $isExpanded={isExpanded} color={tagColor}>
                  <TagHeader color={tagColor} onClick={(e) => onTagExpand(tag.id, e)}>
                    <TagName>
                      <ExpandIcon $isExpanded={isExpanded}>▶</ExpandIcon>
                      <div
                        className="d-flex align-items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selectionMode && (
                          <Form.Check
                            type="checkbox"
                            checked={selectedTagIds?.has(tag.id) || false}
                            onChange={(ev) =>
                              onToggleTagSelection?.(tag.id, ev.currentTarget.checked)
                            }
                            aria-label={`Select tag ${tag.name}`}
                          />
                        )}
                        <span>{tag.name}</span>
                      </div>
                      {isExpanded && (
                        <>
                          <DeleteButton
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowConfirmDeleteAllTags(true)
                            }}
                          >
                            <Trash2 size={16} />
                          </DeleteButton>

                          <ConfirmDialog
                            show={showConfirmDeleteAllTags}
                            onHide={() => setShowConfirmDeleteAllTags(false)}
                            onConfirmRight={() => onRemoveAllTags(tag.id)}
                            title={'Delete All Tags'}
                            message={
                              'Are you sure you want to delete all these tags from this image?'
                            }
                            variant="danger"
                            confirmRightText={'Delete'}
                            cancelText="Cancel"
                          />
                        </>
                      )}
                    </TagName>
                    {showBoxCount && <BoxCount>{tagBoxes.length} boxes</BoxCount>}
                  </TagHeader>
                  <TagContent $isExpanded={isExpanded}>
                    {tagBoxes.map((box, index) => {
                      const isSelected = selectedBBoxClassIndex === box.class_index
                      return (
                        <TagItem
                          key={`${box.class_index}-${index}`}
                          $isSelected={isSelected}
                          color={tagColor}
                          onClick={() => onTagSelect(box)}
                        >
                          <span>
                            {tag.name} {index + 1}
                          </span>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                          >
                            {box.confidence && (
                              <ConfidenceBadge color={tagColor}>
                                {Math.round(box.confidence * 100)}%
                              </ConfidenceBadge>
                            )}
                            {isSelected && (
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onDelete(box)
                                }}
                              >
                                <Trash2 size={16} />
                              </DeleteButton>
                            )}
                          </div>
                        </TagItem>
                      )
                    })}
                  </TagContent>
                </TagGroup>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

export default TagsPanel
